<script>
import { mapGetters } from 'vuex'
import dayjs from '@/services/date'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import formatDate from '@/utils/format-date'
import MenuItem from '@/models/general/menuItem'
import baseMixin from '@/mixins/baseMixin'
import Interaction from '@/models/general/interaction'
import InteractionType from '@/shared/constants/core/InteractionType'
import EmitEvent from '@/models/general/emitEvent'
import BookingCard from '@/shared/constants/booking/BookingCard'
import BaseDropdownMenu from '@/components/_base-dropdown-menu.vue'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingListFeedback from './booking-list-feedback.vue'
import BookingStatusLabel from './booking-status-label.vue'
import ExtendBookingModal from './extend-booking/extend-booking-modal.vue'

export default {
  components: {
    BookingStatusLabel,
    BookingListFeedback,
    BaseDropdownMenu,
    ExtendBookingModal,
  },
  mixins: [baseMixin, featureFlagMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ...VuetifyTextClass,
      textGrey700: VuetifyColorClass.text.grey700,
      showExtendBookingModal: false,
      BookingStatusLabelType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingCreatePermission:
        'auth/hasLocationBookingCreatePermission',
    }),
    cardOptions() {
      const isToday =
        new Date(this.booking.startDateTimeLocal).toDateString() ===
        new Date().toDateString()

      const options = [
        {
          icon: 'mdi-arrow-top-right-bottom-left',
          type: 'action',
          text: this.$t('booking.extend.extend'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'extendBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableExtendBooking,
            this.hasCreateForLocationPerm,
          ],
        },
        {
          id: isToday
            ? 'edit-today-booking-menu-item'
            : 'edit-day-booking-menu-item',
          icon: 'mdi-calendar-edit-outline',
          type: 'action',
          text: this.$t('booking.editBookingDay'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingDay',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.canCreateBookings,
            this.booking.status === 'unfilled' && this.bookingStatus === 'Open',
            new Date(this.booking.startDateTimeLocal) >=
              new Date(new Date().setHours(0, 0, 0, 0)),
          ],
          data: {
            disabled: isToday,
          },
        },
        {
          icon: 'mdi-pencil-outline',
          type: 'action',
          text: this.$t('booking.editBookingBlock'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingBlock',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.canCreateBookings,
            this.booking.status === 'unfilled' && this.bookingStatus === 'Open',
            this.booking.blockDetails &&
              this.booking.blockDetails.bookingsCount ===
                this.booking.blockDetails.openCount &&
              new Date(this.booking.blockDetails.startDateLocal) > new Date(),
          ],
        },
        {
          id: 'duplicate-booking-menu-item',
          icon: 'mdi-content-copy',
          type: 'action',
          text: this.$t('booking.duplicate'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'duplicateBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.canCreateBookings,
          ],
          data: {
            disabled: ![BookingStatusLabelType.Confirmed, BookingStatusLabelType.Pending, BookingStatusLabelType.Open].includes(this.bookingStatus),
          },
        },
        {
          icon: 'mdi-close-circle-outline',
          type: 'action',
          text: this.$t('booking.cancelBookingBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'cancelBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    buttonConfig() {
      const { warning200, blue200, warning, blue, grey500 } = VuetifyColorClass
      return {
        hoverColor: this.isUnfilled ? warning200 : blue200,
        iconColor: grey500,
        iconHoverColor: this.isUnfilled ? warning : blue,
      }
    },
    hasCreateForLocationPerm() {
      const locationId = this.booking?.location?.id

      return locationId && this.hasLocationBookingCreatePermission(locationId)
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    dimensions() {
      const { mobileDimensions, dimensions } = BookingCard
      return this.isSmallOrLower ? mobileDimensions : dimensions
    },
    bookingId() {
      return `#${this.booking.id}`
    },
    blockText() {
      return this.booking?.blockText ? `- ${this.booking.blockText}` : ''
    },
    headerMetaData() {
      return `${this.bookingId} ${this.blockText}`
    },
    candidateName() {
      return this.getName(this.booking.candidate)
    },
    title() {
      return this.candidateName?.length ? this.candidateName : 'Unfilled'
    },
    replacingStaffName() {
      const hasName =
        this.booking.replacing?.firstName?.length ||
        this.booking.replacing?.lastName?.length
      return hasName
        ? this.getName(this.booking.replacing)
        : this.$t('booking.defaultEducatorName')
    },
    bookingStatus() {
      return BookingCard.bookingStatusMap[this.booking.status]
    },
    isUnapproved() {
      return this.bookingStatus === BookingCard.labelStatus.Unapproved
    },
    isUnfilled() {
      return this.bookingStatus === BookingCard.labelStatus.Open
    },
    cardClass() {
      return {
        open: this.isUnfilled,
        unapproved: this.isUnapproved,
        default: !this.isUnfilled && !this.isUnapproved,
      }
    },
    hasCandidate() {
      return this.booking.candidate && this.booking.candidate.id > 0
    },
    formattedStartTime() {
      return formatDate(this.booking.startDateTimeLocal, 'HH:mm')
    },
    formattedEndTime() {
      return formatDate(this.booking.endDateTimeLocal, 'HH:mm')
    },
  },
  methods: {
    getName(person) {
      const { firstName, lastName } = person || {}
      return firstName && lastName ? `${firstName} ${lastName}` : ''
    },
    toggleExtendBookingModal() {
      this.showExtendBookingModal = !this.showExtendBookingModal
    },
    cancelBooking() {
      this.$emit('cancelBooking', this.booking)
    },
    disableMenuItem(menuItem) {
      if (!menuItem) return false

      switch (menuItem.id) {
        case 'edit-today-booking-menu-item':
        case 'duplicate-booking-menu-item':
          return menuItem.data.disabled
        default:
          return false
      }
    },
    editBookingDay() {
      this.$router.push({
        name: 'bookings-edit',
        query: {
          mode: 'single-day',
          bookingId: this.booking.id,
          date: dayjs(this.booking.startDateTimeLocal).format('YYYY-MM-DD'),
        },
      })
    },
    editBookingBlock() {
      this.$router.push({
        name: 'bookings-edit',
        query: {
          mode: 'block',
          bookingId: this.booking.id,
        },
      })
    },
  },
}
</script>

<template>
  <v-sheet outlined :class="['booking-card', cardClass]" v-bind="dimensions">
    <v-row>
      <v-col
        cols="8"
        class="d-flex flex-column justify-space-between"
        :class="[isUnapproved ? 'unapproved-text' : '', textGrey700]"
      >
        <p class="muted-text" :class="smallBody">{{ headerMetaData }}</p>

        <p :class="mediumButton">{{ title }}</p>

        <p>
          <span :class="smallBodyBold">
            {{ `${formattedStartTime} - ${formattedEndTime}` }}
          </span>

          <span class="ml-1 muted-text" :class="smallBody">
            {{ booking.type }}
          </span>
        </p>

        <p
          v-if="isUnapproved"
          :class="[textGrey700, captionBold, 'unaproved-caption']"
        >
          <v-icon class="mr-1 unapproved-caption" size="20">
            mdi-information-outline
          </v-icon>
          {{ $t('booking.pendingSubtitle') }}
        </p>

        <p v-else>
          <span class="muted-text" :class="smallBody">
            {{ `${$t('booking.replacingLabel')}:` }}
          </span>

          <span class="ml-1" :class="smallBodyBold">
            {{ replacingStaffName }}
          </span>
        </p>
      </v-col>

      <v-col
        cols="4"
        class="d-flex flex-column justify-space-between align-end"
      >
        <div
          v-if="canCreateBookings"
          class="d-flex"
          data-button="booking-option"
        >
          <BookingListFeedback
            v-if="hasCandidate && !isUnapproved"
            :booking="booking"
            :button-config="buttonConfig"
          />

          <BaseDropdownMenu
            v-bind="buttonConfig"
            :menu-items="cardOptions"
            tertiary
            :disabled="isUnapproved"
            :disable-list-item="disableMenuItem"
            @editBookingDay="editBookingDay"
            @editBookingBlock="editBookingBlock"
            @cancelBooking="cancelBooking"
            @extendBooking="toggleExtendBookingModal"
          />
        </div>

        <v-spacer v-else />

        <BookingStatusLabel :status="bookingStatus" />
      </v-col>
    </v-row>
    <ExtendBookingModal
      v-model="showExtendBookingModal"
      :booking="booking"
      :is-unfilled="isUnfilled"
    />
  </v-sheet>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.booking-card {
  padding: 12px 16px;
  border-radius: 16px;

  p {
    display: -webkit-box;
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
}

.open {
  background-color: $color-warning-100;
  border: 2px dashed $color-warning-400;
}

.unapproved {
  border: 2px dashed $color-grey-400 !important;
}

.default {
  background-color: $color-grey-200;
  border: 1px solid $color-grey-400;
}

.unapproved-text :not(.unapproved-caption) {
  opacity: 0.38;
}

.muted-text {
  color: $color-grey-600 !important;
}
</style>
